import { ApolloQueryResult, NetworkStatus, useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";

import { CommandPaletteSearchResult } from "components/CommandPalette/types";
import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import { QuerySearchStacksArgs, SearchQueryOrder, SearchQueryPredicate } from "types/generated";
import { uniqByKey } from "utils/uniq";

import {
  SEARCH_STACKS_FOR_NAVIGATION,
  SearchStacksForNavigationData,
  SearchStacksForNavigationItem,
} from "./gql";

type UseStackSearchForNavigationProps = {
  query?: string;
  predicates?: SearchQueryPredicate[];
  orderBy?: SearchQueryOrder[] | null;
  skip?: boolean;
  first?: number;
};

const useSearchStacksForNavigation = ({
  query = "",
  predicates = [],
  orderBy = null,
  skip = false,
  first = 50,
}: UseStackSearchForNavigationProps): CommandPaletteSearchResult<SearchStacksForNavigationItem> & {
  loadMore: () => Promise<void>;
  error: ReturnType<typeof useQuery>["error"];
  refetch: (
    variables?: Partial<QuerySearchStacksArgs> | undefined
  ) => Promise<ApolloQueryResult<SearchStacksForNavigationData>>;
} => {
  const { onError } = useTypedContext(FlashContext);

  const { data, previousData, error, fetchMore, networkStatus, refetch } = useQuery(
    SEARCH_STACKS_FOR_NAVIGATION,
    {
      variables: {
        input: {
          after: null,
          first,
          fullTextSearch: query,
          orderBy,
          requestedPage: null,
          predicates,
        },
      },
      skip,
    }
  );

  const stacks = useMemo(() => {
    return (
      data?.searchStacks?.edges.map((edge) => edge.node) ||
      previousData?.searchStacks?.edges.map((edge) => edge.node) ||
      []
    );
  }, [data, previousData]);

  const hasMore = !!data?.searchStacks?.pageInfo.hasNextPage;
  const endCursor = data?.searchStacks?.pageInfo.endCursor;

  const loadMore = useCallback(async () => {
    if (!hasMore || !endCursor) {
      return;
    }

    try {
      await fetchMore({
        variables: {
          input: {
            first,
            after: endCursor,
            fullTextSearch: query,
            orderBy,
            requestedPage: null,
            predicates,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.searchStacks?.edges?.length) {
            return prev;
          }

          return {
            searchStacks: {
              ...fetchMoreResult.searchStacks,
              edges: uniqByKey(
                [...(prev.searchStacks?.edges || []), ...fetchMoreResult.searchStacks.edges],
                "cursor"
              ),
            },
          };
        },
      });
    } catch (error) {
      onError(error);
    }
  }, [hasMore, endCursor, fetchMore, first, query, orderBy, predicates, onError]);

  const initialLoading = !stacks.length && networkStatus === NetworkStatus.loading;
  const isEmpty = !stacks.length && !initialLoading && !query && !predicates.length;

  return {
    items: stacks,
    loading: initialLoading,
    hasMore,
    isEmpty,
    loadMore,
    error,
    refetch,
  };
};

export default useSearchStacksForNavigation;
